import Head from 'next/head';
import { memo } from 'react';

import { WebsiteStyleTheme } from '../../../../front/src/components/layout/WebsiteStyleTheme';
import { apiUrl } from '../../../../front/src/config';
import { useWebsite } from '../../../../front/src/hooks/useWebsite';
import { html } from '../../../../front/src/utils/dom';
import { MetaPage } from './MetaPage';

type MetaWebsiteProps = {
  noIndex?: boolean;
};

export const MetaWebsite = memo(({ noIndex }: MetaWebsiteProps) => {
  const website = useWebsite();
  const { common = {} } = website?.graphdebate?.meta ?? {};
  const { name, custom = {}, fullBasepathUrl } = website ?? {};
  const { image, faviconArrayHtml = [] } = custom;
  // Flux RSS annoncé seulement en mode presse (route /rss.xml, B6_07) — forum inchangé.
  const isMedia = website?.graphdebate?.mode === 'media';

  // The API serves a dynamic manifest at /images/website/<_id>/manifest.json
  // (see api/src/controllers/website_controller.js:8206). It is included in
  // `faviconArrayHtml`, so we no longer need to inject a static fallback here
  // — the AlphaCircle-specific temporary patch was removed on 2026-05-12.
  const favicons = (faviconArrayHtml as string[])
    .map(fav => fav.replace('/images', `${apiUrl}/images`))
    .join('')
    .replace(/>$/, '/>');

  // Hack to avoid _app to apply indexing to the page if noIndex prop present during SSR
  const metaPageIndexingProps = {
    index: !!!noIndex,
    follow: !!!noIndex,
  };

  return (
    <>
      <WebsiteStyleTheme />

      <Head>
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1.0, shrink-to-fit=no, viewport-fit=cover"
        />

        <meta key="twitter:card" name="twitter:card" content="summary" />
        <meta key="twitter:site" name="twitter:site" content="@graphcomment" />

        {isMedia && fullBasepathUrl ? (
          <link
            key="rss"
            rel="alternate"
            type="application/rss+xml"
            title={name ?? ''}
            href={`${fullBasepathUrl}/rss.xml`}
          />
        ) : null}

        <meta
          key="article:publisher"
          property="article:publisher"
          content="https://www.facebook.com/graphcomment"
        />

        <meta key="og:type" property="og:type" content="website" />
        {name ? (
          <>
            <meta key="og:title" property="og:title" content={name} />
            <meta key="og:description" property="og:description" content={name} />
            <meta key="og:site_name" property="og:site_name" content={name} />
            <meta
              key="og:image"
              property="og:image"
              content={
                !image
                  ? '/img/graphcomment.png'
                  : image.startsWith('/')
                  ? `${apiUrl}${image}`
                  : image
              }
            />
            <meta key="twitter:title" name="twitter:title" content={name} />
            <meta key="twitter:domain" name="twitter:domain" content={name} />
          </>
        ) : null}

        {html(favicons)}
      </Head>
      <MetaPage canonicalPath={undefined} {...common} {...metaPageIndexingProps} />
    </>
  );
});
